// Cajeros: qué usuarios pueden operar cada caja. POS → Configuración → Cajeros.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Caja, CajaUsuario, UsuarioOpt, cajaUsuariosList, cajasActivas, fieldCls, pos, usuariosList } from './shared';

export function Cajeros() {
  const qc = useQueryClient();
  const [cajaId, setCajaId] = useState('');
  const { data: cajas = [] } = useQuery<Caja[]>({ queryKey: ['pos-cajas-activas'], queryFn: cajasActivas });
  const { data: usuarios = [] } = useQuery<UsuarioOpt[]>({ queryKey: ['pos-usuarios'], queryFn: usuariosList, staleTime: Infinity });
  const { data: asignados = [], isFetching } = useQuery<CajaUsuario[]>({
    queryKey: ['pos-caja-usuarios', cajaId], enabled: !!cajaId, queryFn: () => cajaUsuariosList(cajaId),
  });

  const [marc, setMarc] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const m: Record<string, boolean> = {};
    for (const u of usuarios) m[u.id] = asignados.some((a) => a.usuario_id === u.id && a.es_activo);
    setMarc(m);
  }, [usuarios, asignados, cajaId]);

  const guardar = useMutation({
    mutationFn: async () => {
      for (const u of usuarios) {
        const a = asignados.find((x) => x.usuario_id === u.id);
        if (marc[u.id]) {
          if (a) await pos.update('caja_usuarios', a.id, { es_activo: true, deleted_at: null });
          else await pos.create('caja_usuarios', { caja_id: cajaId, usuario_id: u.id, es_activo: true });
        } else if (a && a.es_activo) {
          await pos.update('caja_usuarios', a.id, { es_activo: false });
        }
      }
    },
    onSuccess: () => { toast.success('Cajeros actualizados'); void qc.invalidateQueries({ queryKey: ['pos-caja-usuarios', cajaId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const total = useMemo(() => Object.values(marc).filter(Boolean).length, [marc]);

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Users className="w-5 h-5" /> Cajeros por caja</h1>
      <Card><CardContent className="pt-4 max-w-md">
        <label className="block space-y-1"><span className="text-xs font-medium text-muted-foreground">Caja</span>
          <select className={fieldCls} value={cajaId} onChange={(e) => setCajaId(e.target.value)}>
            <option value="">— Selecciona una caja —</option>
            {cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select></label>
      </CardContent></Card>

      {cajaId && (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Usuarios autorizados ({total}/{usuarios.length})</CardTitle>
            <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Save className="w-4 h-4 mr-1" /> Guardar</Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Usuario</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Autorizado</th></tr></thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 font-medium">{u.username}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{u.email ?? '—'}</td>
                    <td className="py-2 pr-3"><input type="checkbox" className="h-4 w-4 rounded border-input" checked={!!marc[u.id]} onChange={(e) => setMarc((m) => ({ ...m, [u.id]: e.target.checked }))} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-muted-foreground">Solo los usuarios autorizados pueden abrir turno y vender en esta caja. Los administradores (perfil nivel ≥ 5) operan cualquier caja sin asignación.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
